import { TASK_PRIORITY_LABELS, TASK_TYPE_LABELS, type TaskDTO } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, CalendarPlus, CheckCircle2, ListChecks, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { OwnerAvatar, RelativeTime } from '../../components/crm';
import { Badge, Button, Card, Checkbox, EmptyState, Skeleton, Tabs, useToast } from '../../components/ui';
import { useHotkeys } from '../../hooks/useHotkeys';
import { api, apiUrl, errorMessage } from '../../lib/api';
import { pluralize } from '../../lib/format';
import { formatEntryTime } from './entryModel';
import { TaskFormPanel } from './TaskFormPanel';
import { TaskRepeatIcon } from './TaskMeta';
import { invalidateTaskQueries } from './taskQueries';
import { useTaskScope } from './taskScope';

type Due = 'today' | 'overdue' | 'upcoming' | 'no_date' | 'completed';

const DUE_TABS: { key: Due; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'no_date', label: 'No date' },
  { key: 'completed', label: 'Completed' },
];

/** Everything that has to happen, grouped the way a salesperson thinks about their day. */
export function TasksPage() {
  const { scope, canSeeAll, setScope } = useTaskScope();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [due, setDue] = useState<Due>('today');
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TaskDTO | null>(null);

  const status = due === 'completed' ? 'completed' : 'open';
  const query = { due: due === 'completed' ? undefined : due, status, scope, page, pageSize: 50 };
  const { data, isLoading } = useQuery({
    queryKey: ['tasks', 'list', query],
    queryFn: ({ signal }) => api.get<{ items: TaskDTO[]; total: number }>(apiUrl('/api/tasks', query), { signal }),
  });

  const complete = useMutation({
    mutationFn: ({ task, note }: { task: TaskDTO; note?: string }) => api.post(`/api/tasks/${task.id}/complete`, { note }),
    onSuccess: async (_result, { task }) => {
      toast.success('Task completed');
      await invalidateTaskQueries(queryClient, task.businessId);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const reopen = useMutation({
    mutationFn: (task: TaskDTO) => api.post(`/api/tasks/${task.id}/reopen`),
    onSuccess: async (_result, task) => {
      await invalidateTaskQueries(queryClient, task.businessId);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  useHotkeys({ n: () => setFormOpen(true) });

  const items = data?.items ?? [];

  return (
    <Page width="wide">
      <PageHeader
        title="Tasks"
        description={data ? `${pluralize(data.total, 'task')} ${due === 'completed' ? 'completed' : 'open'}` : 'Follow-ups, calls, meetings and reminders.'}
        actions={
          <>
            {canSeeAll && (
              <div className="flex items-center gap-1 rounded-md border border-slate-200 p-0.5">
                <Button size="sm" variant={scope === 'mine' ? 'primary' : 'ghost'} onClick={() => { setScope('mine'); setPage(1); }}>
                  Mine
                </Button>
                <Button size="sm" variant={scope === 'all' ? 'primary' : 'ghost'} onClick={() => { setScope('all'); setPage(1); }}>
                  Everyone
                </Button>
              </div>
            )}
            <Button icon={CalendarDays} onClick={() => void navigate('/calendar')}>
              Open calendar
            </Button>
            <Button variant="primary" icon={Plus} onClick={() => setFormOpen(true)}>
              New task
            </Button>
          </>
        }
      />

      <Card>
        <Tabs
          tabs={DUE_TABS.map((tab) => ({ key: tab.key, label: tab.label }))}
          value={due}
          onChange={(key) => {
            setDue(key as Due);
            setPage(1);
          }}
        />
        <div className="p-4">
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : items.length === 0 ? (
            <EmptyState
              icon={ListChecks}
              title={due === 'completed' ? 'No completed tasks yet' : 'Nothing here'}
              description={
                due === 'today'
                  ? 'No tasks are due today. Schedule the next step on a lead so it does not slip.'
                  : due === 'overdue'
                    ? 'Nothing is late.'
                    : 'Nothing matches this view.'
              }
              action={<Button icon={CalendarPlus} onClick={() => setFormOpen(true)}>Add a task</Button>}
            />
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((task) => (
                <li key={task.id} className="flex flex-wrap items-center gap-2 py-2.5 text-[13px]">
                  <Checkbox
                    aria-label={`${task.status === 'completed' ? 'Reopen' : 'Complete'} ${task.title}`}
                    checked={task.status === 'completed'}
                    onChange={() => (task.status === 'completed' ? reopen.mutate(task) : complete.mutate({ task }))}
                  />
                  <button type="button" className="min-w-0 font-medium text-slate-900 hover:text-ember-700" onClick={() => setEditing(task)}>
                    {task.title}
                  </button>
                  <TaskRepeatIcon task={task} />
                  {task.businessId && (
                    <Link to={`/leads/${task.businessId}`} className="truncate text-slate-500 hover:text-ember-700">
                      {task.businessName}
                    </Link>
                  )}
                  <Badge color={task.priority === 'urgent' || task.priority === 'high' ? 'amber' : 'slate'} size="sm">
                    {TASK_PRIORITY_LABELS[task.priority]}
                  </Badge>
                  <span className="text-slate-500">{TASK_TYPE_LABELS[task.type]}</span>
                  {task.dueAt && (
                    <span className={due === 'overdue' ? 'text-red-600' : 'text-slate-500'}>
                      {formatEntryTime(task)} · <RelativeTime value={task.dueAt} granularity="day" />
                    </span>
                  )}
                  {task.status === 'completed' && task.completedAt && (
                    <span className="flex items-center gap-1 text-emerald-700">
                      <CheckCircle2 className="size-3.5" /> <RelativeTime value={task.completedAt} />
                    </span>
                  )}
                  <span className="ml-auto flex items-center gap-2">
                    <OwnerAvatar userId={task.assignedTo} showName />
                  </span>
                </li>
              ))}
            </ul>
          )}
          {data && data.total > items.length && (
            <div className="mt-3 flex justify-center">
              <Button onClick={() => setPage((current) => current + 1)}>Load more</Button>
            </div>
          )}
        </div>
      </Card>

      {(formOpen || editing) && (
        <TaskFormPanel
          task={editing ?? undefined}
          onClose={() => {
            setFormOpen(false);
            setEditing(null);
          }}
        />
      )}
    </Page>
  );
}
